import { formatDataLine, parseMarkdown } from 'services/imports/markdownContract';
import type { LineSource, MarkdownLine } from 'services/imports/markdownContract';
import type { AmendmentSuggestion } from 'services/imports/reviewAmendments';
import { findAmendmentTarget } from 'views/imports/utils';
import { DATE_KEY_RE, normalizeDateValue } from 'services/imports/dateValues';
import { splitChargePayees } from 'services/imports/chargeDescriptions';

/** Value and source as they were first found (or as last accepted), used by Reset. */
export type LineSnapshot = { key: string; value: string; src: LineSource | null };

/**
 * The imports document. `markdown` is what the assistant receives; every data line
 * carries a stable id (`r<n>`) so edits, resets and removals address the same row.
 */
export type ImportsDoc = {
  markdown: string;
  originals: Readonly<Record<string, LineSnapshot>>;
  removed: Readonly<Record<string, LineSnapshot>>;
  nextId: number;
};

export const EMPTY_DOC: ImportsDoc = {
  markdown: '',
  originals: {},
  removed: {},
  nextId: 1,
};

const LINE_BREAK_RE = /\r\n|\n|\r/;

/** Row ids are for the screen only; the assistant and the read-only markdown view never show them. */
const ROW_ID_RE = / ?<!-- id: [A-Za-z0-9_-]+ -->/g;

export const stripRowIds = (markdown: string): string => markdown.replace(ROW_ID_RE, '');
const DATA_HEADING_RE = /^#{1,2}\s+data\s*$/i;

const numberOf = (id: string): number => Number(id.slice(1));

const parsedLines = (markdown: string): MarkdownLine[] => parseMarkdown(markdown).lines;

const dataLineById = (markdown: string, id: string): MarkdownLine | null => (
  parsedLines(markdown).find((line) => line.id === id) ?? null
);

const replaceLineAt = (markdown: string, lineNo: number, next: Parameters<typeof formatDataLine>[0]): string => {
  const lines = markdown.split(LINE_BREAK_RE);
  lines[lineNo - 1] = formatDataLine(next);
  return lines.join('\n');
};

const deleteLineAt = (markdown: string, lineNo: number): string => markdown
  .split(LINE_BREAK_RE)
  .filter((_, index) => index !== lineNo - 1)
  .join('\n');

/**
 * Inserts a data line in id order: before the first line with a larger id, otherwise
 * after the last data line. Creates the Data section if the document has none.
 */
const insertDataLine = (markdown: string, text: string, afterNumber?: number): string => {
  const lines = markdown.split(LINE_BREAK_RE);
  const data = parsedLines(markdown);
  if (afterNumber !== undefined) {
    const next = data.find((line) => line.id !== null && numberOf(line.id) > afterNumber);
    if (next) {
      lines.splice(next.lineNo - 1, 0, text);
      return lines.join('\n');
    }
  }
  const last = data[data.length - 1];
  if (last) {
    lines.splice(last.lineNo, 0, text);
    return lines.join('\n');
  }
  const heading = lines.findIndex((line) => DATA_HEADING_RE.test(line));
  if (heading >= 0) {
    lines.splice(heading + 1, 0, text);
    return lines.join('\n');
  }
  return [...lines.filter((line) => line.trim() !== ''), '', '## Data', text, ''].join('\n');
};

const sourceLabel = (src: LineSource | null): string => (src ? `${src.file} p${src.page} ${src.line}` : 'no source');

/** Gives every data line that lacks an id a fresh id and records its current value as the original. */
export const withLineIds = (doc: ImportsDoc): ImportsDoc => {
  const data = parsedLines(doc.markdown);
  const usedIds = [
    ...data.filter((line) => line.id !== null).map((line) => numberOf(line.id ?? 'r0')),
    ...Object.keys(doc.removed).map(numberOf),
  ];
  let nextId = Math.max(doc.nextId, ...usedIds.map((n) => n + 1));
  const lines = doc.markdown.split(LINE_BREAK_RE);
  const originals: Record<string, LineSnapshot> = { ...doc.originals };
  let changed = false;

  data.forEach((line) => {
    if (line.status === 'malformed' || line.id !== null) return;
    const id = `r${nextId}`;
    nextId += 1;
    lines[line.lineNo - 1] = formatDataLine({
      key: line.key, value: line.value, src: line.src, id,
    });
    originals[id] = { key: line.key, value: line.value, src: line.src };
    changed = true;
  });

  if (!changed) return { ...doc, nextId };
  return {
    ...doc,
    markdown: lines.join('\n'),
    originals,
    nextId,
  };
};

const appendLine = (doc: ImportsDoc, incoming: { key: string; value: string; src: LineSource | null }): ImportsDoc => ({
  ...doc,
  markdown: insertDataLine(doc.markdown, formatDataLine(incoming)),
});

/** A line is edited when its value differs from the original. */
export const isEdited = (doc: ImportsDoc, line: MarkdownLine): boolean => {
  if (line.id === null) return false;
  const original = doc.originals[line.id];
  return original !== undefined && original.value !== line.value;
};

export const setLineValue = (doc: ImportsDoc, id: string, value: string, src?: LineSource | null): ImportsDoc => {
  const line = dataLineById(doc.markdown, id);
  if (line === null) return doc;
  return {
    ...doc,
    markdown: replaceLineAt(doc.markdown, line.lineNo, {
      key: line.key,
      value,
      src: src === undefined ? line.src : src,
      id,
    }),
  };
};

const setValueAt = (doc: ImportsDoc, line: MarkdownLine, value: string): ImportsDoc => ({
  ...doc,
  markdown: replaceLineAt(doc.markdown, line.lineNo, { key: line.key, value, src: line.src }),
});

/** Sets the first data line with `key`, or adds it when absent (used by the order form). */
export const setValueByKey = (doc: ImportsDoc, key: string, value: string): ImportsDoc => {
  const line = parsedLines(doc.markdown).find((candidate) => candidate.key === key && candidate.status !== 'malformed');
  if (line === undefined) return withLineIds(appendLine(doc, { key, value, src: null }));
  if (line.id === null) return withLineIds(setValueAt(doc, line, value));
  return setLineValue(doc, line.id, value);
};

/** Restores the value and source the line had when it was first found. */
export const resetLine = (doc: ImportsDoc, id: string): ImportsDoc => {
  const original = doc.originals[id];
  if (original === undefined) return doc;
  return setLineValue(doc, id, original.value, original.src);
};

/** Takes a data line out of the markdown. Removed rows are kept so they can be restored. */
export const removeLine = (doc: ImportsDoc, id: string): ImportsDoc => {
  const line = dataLineById(doc.markdown, id);
  if (line === null) return doc;
  return {
    ...doc,
    markdown: deleteLineAt(doc.markdown, line.lineNo),
    removed: { ...doc.removed, [id]: { key: line.key, value: line.value, src: line.src } },
  };
};

/** Malformed lines have no id and cannot be restored; they are removed outright. */
export const removeMalformedLine = (doc: ImportsDoc, lineNo: number): ImportsDoc => ({
  ...doc,
  markdown: deleteLineAt(doc.markdown, lineNo),
});

export const restoreLine = (doc: ImportsDoc, id: string): ImportsDoc => {
  const snapshot = doc.removed[id];
  if (snapshot === undefined) return doc;
  const removed = { ...doc.removed };
  delete removed[id];
  return {
    ...doc,
    removed,
    markdown: insertDataLine(doc.markdown, formatDataLine({ ...snapshot, id }), numberOf(id)),
  };
};

/** Sending hands the markdown to the assistant, so removed rows are no longer restorable. */
export const forgetRemoved = (doc: ImportsDoc): ImportsDoc => ({ ...doc, removed: {} });

/**
 * Merges a fresh extraction into the document. A key already present is updated in
 * place, unless the reviewer edited it: the edit wins and the key is reported as skipped.
 * `note` lines and new keys are appended.
 */
export const mergeExtraction = (
  doc: ImportsDoc,
  extracted: string,
  now: Date = new Date(),
): { doc: ImportsDoc; skipped: string[] } => {
  let current = withLineIds(doc);
  const skipped: string[] = [];
  // A payee named in a charge description moves to the charge's own payee field.
  const incomingLines = splitChargePayees(parsedLines(extracted).filter((line) => line.status !== 'malformed'));
  incomingLines.forEach((line) => {
    // Dates are stored in local time; a date without a known time gets the current time, never midnight.
    const incoming = {
      key: line.key,
      value: DATE_KEY_RE.test(line.key) ? normalizeDateValue(line.value, now) : line.value,
      src: line.src,
    };
    const existing = parsedLines(current.markdown).find((candidate) => (
      candidate.key === incoming.key && candidate.status !== 'malformed' && candidate.id !== null
    ));
    const removedByReviewer = Object.values(current.removed).some((removed) => removed.key === incoming.key);
    if (incoming.key !== 'note' && removedByReviewer) {
      skipped.push(incoming.key);
      return;
    }
    const duplicateNote = incoming.key === 'note' && parsedLines(current.markdown).some((note) => note.key === 'note' && note.value === incoming.value);
    if (duplicateNote) return;
    if (incoming.key === 'note' || existing === undefined || existing.id === null) {
      current = appendLine(current, incoming);
      return;
    }
    if (isEdited(current, existing)) {
      skipped.push(incoming.key);
      return;
    }
    const src = incoming.src ?? existing.src;
    current = setLineValue(current, existing.id, incoming.value, src);
    current = {
      ...current,
      originals: { ...current.originals, [existing.id]: { key: incoming.key, value: incoming.value, src } },
    };
  });
  return { doc: withLineIds(current), skipped };
};

/**
 * Applies an accepted amendment. The row keeps its id, its original becomes the accepted
 * value, and the replaced value is recorded in a note.
 */
export const applyAmendmentToDoc = (doc: ImportsDoc, amendment: AmendmentSuggestion): ImportsDoc => {
  const current = withLineIds(doc);
  const value = DATE_KEY_RE.test(amendment.key) ? normalizeDateValue(amendment.value) : amendment.value;
  const target = findAmendmentTarget(parsedLines(current.markdown), amendment);
  if (target === null || target.id === null) {
    return withLineIds(appendLine(current, { key: amendment.key, value, src: amendment.src }));
  }
  const src = amendment.src ?? target.src;
  const replaced = setLineValue(current, target.id, value, src);
  const note = `Amended ${target.key} from "${target.value}" (${sourceLabel(target.src)})`;
  return withLineIds({
    ...appendLine(replaced, { key: 'note', value: note, src: amendment.src }),
    originals: {
      ...replaced.originals,
      [target.id]: { key: target.key, value, src },
    },
  });
};
